import { useEffect, useState } from "react";
import { useContent } from "../content/ContentContext";
import { money } from "../lib/format";

const iso = (d) => d.toISOString().slice(0, 10);

const defaults = (site) => {
  const issue = new Date();
  const due = new Date();
  due.setDate(due.getDate() + 14);
  return {
    fromName: site.businessName,
    fromDetails: `${site.owner}\nFrance\nEmail: ${site.email}\n[Business registration details — add before issuing]`,
    clientName: "[Client name]",
    clientDetails: "[Client address lines]",
    number: `SS-${issue.getFullYear()}-001`,
    issue: iso(issue),
    due: iso(due),
    currency: "EUR",
    items: [{ desc: "Hourly consulting & contract support (hours)", qty: 3, rate: 60 }],
    taxLabel: "Tax / VAT (status to confirm)",
    taxRate: 0,
    paymentDetails: "[Payment details — add your bank or payment information before issuing]",
    paymentTerms: "Payment due within 14 days of the invoice date.",
    notes: "Thank you for your business.",
  };
};

const STORAGE_KEY = "ss_invoice_v1";
const inputCls =
  "w-full rounded-xl border border-line bg-white px-3 py-2 text-sm text-ink transition-colors focus:border-accent";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-medium text-muted">{label}</span>
      {children}
    </label>
  );
}

export default function Invoice() {
  const { content } = useContent();
  const [state, setState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch {}
    return null;
  });
  const inv = state || defaults(content.site);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(inv));
  }, [inv]);

  const set = (k, v) => setState((f) => ({ ...(f || inv), [k]: v }));
  const setItem = (i, k, v) =>
    setState((f) => ({
      ...(f || inv),
      items: (f ? f.items : inv.items).map((it, idx) => (idx === i ? { ...it, [k]: v } : it)),
    }));

  const subtotal = inv.items.reduce((sum, it) => sum + Number(it.qty || 0) * Number(it.rate || 0), 0);
  const taxAmount = (subtotal * Number(inv.taxRate || 0)) / 100;
  const total = subtotal + taxAmount;

  return (
    <div>
      <div className="no-print grid grid-cols-1 gap-6 lg:grid-cols-2" data-testid="invoice-editor">
        <div className="rounded-2xl border border-line bg-white p-6 sm:p-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent">Your details</p>
          <div className="mt-4 space-y-4">
            <Field label="Business name">
              <input data-testid="invoice-from-name" className={inputCls} value={inv.fromName} onChange={(e) => set("fromName", e.target.value)} />
            </Field>
            <Field label="Your details (address, registration — one per line)">
              <textarea data-testid="invoice-from-details" rows={4} className={inputCls} value={inv.fromDetails} onChange={(e) => set("fromDetails", e.target.value)} />
            </Field>
            <Field label="Currency (manual — no automatic conversion)">
              <select data-testid="invoice-currency-select" className={inputCls} value={inv.currency} onChange={(e) => set("currency", e.target.value)}>
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
              </select>
            </Field>
          </div>
        </div>
        <div className="rounded-2xl border border-line bg-white p-6 sm:p-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent">Invoice details</p>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Client name">
              <input data-testid="invoice-client-name" className={inputCls} value={inv.clientName} onChange={(e) => set("clientName", e.target.value)} />
            </Field>
            <Field label="Invoice number">
              <input data-testid="invoice-number" className={inputCls} value={inv.number} onChange={(e) => set("number", e.target.value)} />
            </Field>
            <Field label="Client details">
              <textarea data-testid="invoice-client-details" rows={3} className={inputCls} value={inv.clientDetails} onChange={(e) => set("clientDetails", e.target.value)} />
            </Field>
            <div className="space-y-4">
              <Field label="Issue date">
                <input data-testid="invoice-issue-date" type="date" className={inputCls} value={inv.issue} onChange={(e) => set("issue", e.target.value)} />
              </Field>
              <Field label="Due date">
                <input data-testid="invoice-due-date" type="date" className={inputCls} value={inv.due} onChange={(e) => set("due", e.target.value)} />
              </Field>
            </div>
          </div>
        </div>
        <div className="rounded-2xl border border-line bg-white p-6 sm:p-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent">Line items</p>
          <div className="mt-4 space-y-3">
            {inv.items.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2" data-testid="invoice-line-item">
                <input data-testid={`invoice-item-desc-${i}`} placeholder="Description" className={`${inputCls} col-span-7`} value={it.desc} onChange={(e) => setItem(i, "desc", e.target.value)} />
                <input data-testid={`invoice-item-qty-${i}`} type="number" min="0" step="0.5" placeholder="Qty" className={`${inputCls} col-span-2`} value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} />
                <input data-testid={`invoice-item-rate-${i}`} type="number" min="0" step="0.01" placeholder="Rate" className={`${inputCls} col-span-2`} value={it.rate} onChange={(e) => setItem(i, "rate", e.target.value)} />
                <button
                  data-testid={`invoice-item-remove-${i}`}
                  onClick={() => setState((f) => ({ ...(f || inv), items: f.items.filter((_, idx) => idx !== i) }))}
                  className="col-span-1 rounded-xl border border-line text-xs font-semibold text-muted hover:border-plum hover:text-plum"
                  aria-label={`Remove line ${i + 1}`}
                >
                  ×
                </button>
              </div>
            ))}
            <button
              data-testid="invoice-add-line"
              onClick={() => setState((f) => ({ ...(f || inv), items: [...f.items, { desc: "", qty: 1, rate: 0 }] }))}
              className="rounded-full border border-line px-4 py-2 text-xs font-semibold text-ink hover:border-plum hover:text-plum"
            >
              + Add line
            </button>
          </div>
        </div>
        <div className="rounded-2xl border border-line bg-white p-6 sm:p-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent">Tax, payment & notes</p>
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Tax label">
                <input data-testid="invoice-tax-label" className={inputCls} value={inv.taxLabel} onChange={(e) => set("taxLabel", e.target.value)} />
              </Field>
              <Field label="Tax rate %">
                <input data-testid="invoice-tax-rate" type="number" min="0" step="0.1" className={inputCls} value={inv.taxRate} onChange={(e) => set("taxRate", e.target.value)} />
              </Field>
            </div>
            <Field label="Payment details">
              <textarea data-testid="invoice-payment-details" rows={2} className={inputCls} value={inv.paymentDetails} onChange={(e) => set("paymentDetails", e.target.value)} />
            </Field>
            <Field label="Payment terms">
              <input data-testid="invoice-payment-terms" className={inputCls} value={inv.paymentTerms} onChange={(e) => set("paymentTerms", e.target.value)} />
            </Field>
            <Field label="Notes">
              <textarea data-testid="invoice-notes" rows={2} className={inputCls} value={inv.notes} onChange={(e) => set("notes", e.target.value)} />
            </Field>
            <div className="flex flex-wrap gap-3 pt-2">
              <button
                data-testid="invoice-print-button"
                onClick={() => window.print()}
                className="rounded-full bg-plum px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-ink"
              >
                Print / Save as PDF
              </button>
              <button
                data-testid="invoice-reset-button"
                onClick={() => {
                  if (window.confirm("Reset the invoice to the template defaults?")) setState(defaults(content.site));
                }}
                className="rounded-full border border-line px-6 py-3 text-sm font-semibold text-ink hover:border-plum hover:text-plum"
              >
                Reset template
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Printable sheet */}
      <div className="doc-sheet print-area mx-auto mt-10 max-w-[820px] rounded-2xl border border-line p-8 sm:p-12" data-testid="invoice-sheet">
        <div className="flex flex-wrap items-start justify-between gap-6 border-b-2 border-plum pb-6">
          <div>
            <p className="font-serif text-2xl font-bold text-plum">
              ss<span className="text-accent">.</span>
            </p>
            <p className="mt-2 font-serif text-lg font-semibold text-ink">{inv.fromName}</p>
            <p className="mt-2 whitespace-pre-line text-xs leading-relaxed text-muted">{inv.fromDetails}</p>
          </div>
          <div className="text-right">
            <p className="font-serif text-3xl font-semibold tracking-tight text-ink">Invoice</p>
            <p className="mt-1 font-mono text-sm text-plum">{inv.number}</p>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-8">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent">Bill to</p>
            <p className="mt-2 text-sm font-semibold text-ink">{inv.clientName}</p>
            <p className="mt-1 whitespace-pre-line text-xs leading-relaxed text-muted">{inv.clientDetails}</p>
          </div>
          <div className="text-right text-sm">
            <p className="text-muted">
              Issue date: <span className="font-medium text-ink">{inv.issue}</span>
            </p>
            <p className="mt-1 text-muted">
              Due date: <span className="font-medium text-ink">{inv.due}</span>
            </p>
            <p className="mt-1 text-muted">
              Currency: <span className="font-medium text-ink">{inv.currency}</span>
            </p>
          </div>
        </div>

        <table className="mt-8 w-full border-collapse text-left text-sm">
          <thead>
            <tr className="border-b border-line font-mono text-[10px] uppercase tracking-[0.14em] text-accent">
              <th className="py-2.5 pr-3 font-medium">Description</th>
              <th className="py-2.5 pr-3 text-right font-medium">Qty</th>
              <th className="py-2.5 pr-3 text-right font-medium">Rate</th>
              <th className="py-2.5 text-right font-medium">Amount</th>
            </tr>
          </thead>
          <tbody>
            {inv.items.map((it, i) => (
              <tr key={i} className="border-b border-line/70">
                <td className="py-2.5 pr-3 text-ink">{it.desc || "—"}</td>
                <td className="py-2.5 pr-3 text-right text-ink">{it.qty}</td>
                <td className="py-2.5 pr-3 text-right text-ink">{money(it.rate, inv.currency)}</td>
                <td className="py-2.5 text-right font-medium text-ink">{money(it.qty * it.rate, inv.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-6 flex justify-end">
          <div className="w-full max-w-xs text-sm">
            <p className="flex justify-between py-1 text-muted">
              <span>Subtotal</span>
              <span className="font-medium text-ink">{money(subtotal, inv.currency)}</span>
            </p>
            <p className="flex justify-between py-1 text-muted">
              <span>
                {inv.taxLabel} ({inv.taxRate}%)
              </span>
              <span className="font-medium text-ink">{money(taxAmount, inv.currency)}</span>
            </p>
            <p className="mt-2 flex justify-between border-t-2 border-plum py-2 font-serif text-lg font-semibold text-plum">
              <span>Total</span>
              <span>{money(total, inv.currency)}</span>
            </p>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 border-t border-line pt-6 text-xs leading-relaxed sm:grid-cols-2">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent">Payment details</p>
            <p className="mt-2 whitespace-pre-line text-muted">{inv.paymentDetails}</p>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent">Terms & notes</p>
            <p className="mt-2 text-muted">{inv.paymentTerms}</p>
            <p className="mt-2 whitespace-pre-line text-muted">{inv.notes}</p>
          </div>
        </div>

        <p className="mt-8 border-t border-line pt-4 text-[10px] italic text-muted">
          Draft template — confirm your business registration, tax status and applicable
          invoicing requirements before issuing.
        </p>
      </div>
    </div>
  );
}
